import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Button,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FaCheckCircle, FaBatteryFull, FaDollarSign, FaShieldAlt, FaChartLine, FaHome } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const BatteryInstallationPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />

      <Box bg={getColor("background.main")} minHeight="100vh" position="relative" overflow="hidden">
        {/* Hero Section */}
        <Box
          position="relative"
          width="100vw"
          height="500px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/services/residential_solar.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={1} />
          <Container maxW="1200px" position="relative" zIndex={2} height="100%" display="flex" alignItems="center">
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Battery Installation
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
              >
                Store your solar energy and achieve true energy independence with hybrid battery systems
              </Text>
              <Button
                size="lg"
                bg="white"
                color="#121c47"
                px={8}
                py={6}
                fontSize="md"
                fontWeight="600"
                textTransform="uppercase"
                borderRadius="4px"
                fontFamily="'Open Sans', sans-serif"
                _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.3s"
                onClick={() => navigate("/contact")}
              >
                Get Free Quote
              </Button>
            </VStack>
          </Container>
        </Box>

        {/* Key Benefits Banner */}
        <Box bg="#121c47" py={{ base: 8, md: 12 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaBatteryFull size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Energy Storage
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaShieldAlt size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Backup Power
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaDollarSign size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Save More
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaHome size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Energy Independence
                </Text>
              </Flex>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Main Content */}
        <Box bg="white" py={{ base: 16, md: 20 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              {/* Introduction */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
                <Box>
                  <Box width="60px" height="2px" bg="#121c47" mb={4} />
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color="#050303"
                    fontFamily="'Open Sans', sans-serif"
                    mb={6}
                  >
                    Maximize Your Solar Investment with Battery Storage
                  </Heading>
                  <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>
                    Hybrid solar battery systems represent the future of home energy in New Zealand. By storing excess solar energy generated during the day, you can power your home through the night and during grid outages, reducing your reliance on the grid and maximizing your solar savings.
                  </Text>
                  <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                    With falling feed-in tariffs and rising electricity costs, now is the perfect time to add battery storage to your solar system and take control of your energy future.
                  </Text>
                </Box>
                <Box>
                  <Image
                    src="/assets/onesolarimages/services/residential_solar.jpg"
                    alt="Battery Installation"
                    width="100%"
                    height="auto"
                    borderRadius="lg"
                    objectFit="cover"
                  />
                </Box>
              </SimpleGrid>

              {/* What is a Hybrid Battery System */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  What is a Hybrid Solar Battery System?
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>
                  A hybrid solar battery system combines solar panels, battery storage, and grid connectivity into one intelligent solution. During the day, your solar panels generate electricity that powers your home. Any excess energy is stored in your battery for use at night or during peak pricing periods.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                  When your battery is full and your home's energy needs are met, any remaining surplus power is exported to the grid. If your battery runs low, the system seamlessly draws power from the grid, ensuring you always have electricity when you need it.
                </Text>
              </Box>

              {/* Financial Benefits */}
              <Box bg="#f8f9fa" p={{ base: 6, md: 8 }} borderRadius="lg">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                  textAlign="center"
                >
                  Smart Financial Investment
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" textAlign="center" maxW="900px" mx="auto" mb={4}>
                  With typical payback periods of <strong>5-8 years</strong> and system lifespans of 25+ years, battery storage is a proven long-term investment. Store your solar energy instead of selling it back to the grid for minimal returns, and use it when electricity prices are highest.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" textAlign="center" maxW="900px" mx="auto">
                  Many New Zealand households can achieve <strong>80-90% energy independence</strong> with a properly sized hybrid battery system, dramatically reducing their electricity bills year after year.
                </Text>
              </Box>

              {/* Key Benefits */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Why Add Battery Storage to Your Solar System?
                </Heading>
                <List spacing={4}>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Use Your Solar Power Day and Night
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Store excess solar energy generated during the day and use it at night, maximizing your self-consumption and reducing grid reliance
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Protection During Power Outages
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Keep your lights on and essential appliances running during grid outages with seamless automatic switchover to battery backup
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Reduce Peak-Time Energy Costs
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Avoid expensive peak electricity rates by using stored solar energy during high-cost periods, especially valuable on time-of-use tariffs
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Increase Energy Independence
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Reduce your dependence on the grid and protect yourself from rising electricity prices with greater energy self-sufficiency
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Future-Proof Your Home
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Be ready for emerging technologies like time-of-use tariffs, virtual power plants (VPPs), and electric vehicle charging integration
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Enhance Your Property Value
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Homes with solar and battery systems are increasingly desirable, offering energy security and lower running costs to future buyers
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                </List>
              </Box>

              {/* How It Works */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  How Hybrid Battery Systems Work
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                  >
                    <Text fontSize="4xl" mb={3}>☀️</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      1. Solar Generation
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Your solar panels generate electricity during daylight hours, powering your home and appliances in real-time
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                  >
                    <Text fontSize="4xl" mb={3}>🔋</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      2. Energy Storage
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Excess solar energy is automatically stored in your battery system for use when the sun isn't shining
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                  >
                    <Text fontSize="4xl" mb={3}>🏠</Text>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      3. Smart Power Management
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Your system intelligently manages energy flow, using stored battery power at night and only drawing from the grid when necessary
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Battery Options */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Finding Your Perfect Battery Solution
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={6}>
                  Every home is unique, and so are your energy needs. That's why we work with leading battery manufacturers to offer a range of premium energy storage solutions tailored to New Zealand conditions. Whether you're looking for backup power during outages, maximum energy independence, or the best return on investment, we'll help you find the perfect match.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Box bg="#f8f9fa" p={6} borderRadius="lg" textAlign="center">
                    <Box fontSize="4xl" mb={4}>⚡</Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Power Capacity
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      We'll analyze your household energy consumption to recommend the right battery capacity, ensuring you have enough stored energy to power through the night and backup essential loads during outages
                    </Text>
                  </Box>
                  <Box bg="#f8f9fa" p={6} borderRadius="lg" textAlign="center">
                    <Box fontSize="4xl" mb={4}>🔒</Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Reliability & Warranty
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      All our battery solutions come with comprehensive manufacturer warranties and proven track records in New Zealand conditions, giving you peace of mind for years to come
                    </Text>
                  </Box>
                  <Box bg="#f8f9fa" p={6} borderRadius="lg" textAlign="center">
                    <Box fontSize="4xl" mb={4}>💰</Box>
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Value & Performance
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      We consider your budget, expected payback period, and long-term energy goals to recommend a solution that delivers the best value and performance for your investment
                    </Text>
                  </Box>
                </SimpleGrid>
                <Box mt={8} bg="#f0f7ff" p={6} borderRadius="lg" border="2px solid" borderColor="#121c47">
                  <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3} textAlign="center">
                    Ready to Explore Your Battery Options?
                  </Heading>
                  <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" textAlign="center" mb={4}>
                    Our solar experts are ready to discuss which battery solution is right for your home. We'll walk you through the latest technology, compare options suited to your needs, and provide transparent pricing with detailed savings projections.
                  </Text>
                  <Flex justify="center">
                    <Button
                      size="lg"
                      bg="#121c47"
                      color="white"
                      px={8}
                      py={6}
                      fontSize="md"
                      fontWeight="600"
                      textTransform="uppercase"
                      borderRadius="4px"
                      fontFamily="'Open Sans', sans-serif"
                      _hover={{ bg: "#1a2659", transform: "translateY(-2px)", boxShadow: "lg" }}
                      transition="all 0.3s"
                      onClick={() => navigate("/contact")}
                    >
                      Let's Talk About Batteries
                    </Button>
                  </Flex>
                </Box>
              </Box>

              {/* Installation Process */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Professional Battery Installation Process
                </Heading>
                <List spacing={4}>
                  <ListItem>
                    <Flex align="flex-start">
                      <Box
                        minW="40px"
                        minH="40px"
                        borderRadius="full"
                        bg="#121c47"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="600"
                        mr={4}
                      >
                        1
                      </Box>
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Free Energy Assessment
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          We analyze your current solar system, energy usage patterns, and electricity bills to determine the optimal battery size and configuration
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <Box
                        minW="40px"
                        minH="40px"
                        borderRadius="full"
                        bg="#121c47"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="600"
                        mr={4}
                      >
                        2
                      </Box>
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          System Design & Quotation
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Our solar designers create a customized battery solution with detailed financial projections showing your expected savings and payback period
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <Box
                        minW="40px"
                        minH="40px"
                        borderRadius="full"
                        bg="#121c47"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="600"
                        mr={4}
                      >
                        3
                      </Box>
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Professional Installation
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Our certified electricians and solar installers complete the installation to the highest standards, ensuring seamless integration with your existing solar system
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <Box
                        minW="40px"
                        minH="40px"
                        borderRadius="full"
                        bg="#121c47"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="600"
                        mr={4}
                      >
                        4
                      </Box>
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          System Testing & Handover
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          We thoroughly test your battery system, configure monitoring apps, and provide comprehensive training on how to maximize your energy savings
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                </List>
              </Box>

              {/* CTA Section */}
              <Box
                bg="#121c47"
                color="white"
                p={{ base: 8, md: 12 }}
                borderRadius="lg"
                textAlign="center"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                  fontWeight="bold"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                >
                  Ready to Achieve Energy Independence?
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  maxW="700px"
                  mx="auto"
                >
                  Contact One Solar New Zealand today for a free battery assessment. Our expert team will design a battery solution that maximizes your solar investment and energy savings.
                </Text>
                <Button
                  size="lg"
                  bg="white"
                  color="#121c47"
                  px={8}
                  py={6}
                  fontSize="md"
                  fontWeight="600"
                  textTransform="uppercase"
                  borderRadius="4px"
                  fontFamily="'Open Sans', sans-serif"
                  _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.3s"
                  onClick={() => navigate("/contact")}
                >
                  Get Your Free Assessment
                </Button>
              </Box>
            </VStack>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default BatteryInstallationPage;
